import { createContext } from 'preact';
import { Context } from '../common/context.js';
import { SiteData } from './templates/site.js';
import { AccountAccess, OwnAccount } from '../common/data/accounts.js';
import { OwnUser } from '../common/data/users.js';
import { Maybe } from '../common/data.js';
import { Tier } from '../common/data/stores.js';
import { PublicDomain } from '../common/data/domains.js';
import { MenuEntries, MenuEntry } from '../common/data/menus.js';
import { signal, Signal } from '@preact/signals';
import { ExpandedCart } from '../common/data/cart.js';

export interface SiteContextInit extends SiteData {
	
	/**
	 * Explicitly omit all the volatile things here.
	 */
	context: Omit<Context, 'user' | 'account' | 'domain'>;
	
	domain: PublicDomain;
	account: Maybe<OwnAccount>;
	user: Maybe<OwnUser>;
	
	cart: Maybe<ExpandedCart>;
	
	isServerSide: boolean;
	
}

export type Signalize<T> = { [K in keyof T]: Signal<T[K]> };
export type Designalize<T> = { [K in keyof T]: T[K] extends Signal<infer U> ? U : T[K] };

export const signalize = <T extends object> (it: T) => Object.fromEntries(Object.entries(it).map(([k, v]) => [k, signal(v)])) as Signalize<T>;
export const designalize = <T extends { [K in keyof any]: Signal<any> }> (it: T) => Object.fromEntries(Object.entries(it).map(([k, v]) => [k, v.value])) as Designalize<T>;

export interface SiteContextData extends SiteContextInit {
	
	userMenus: MenuEntries;
	auxMenu: MenuEntry;
	isDesktop: Maybe<boolean>;
	sidebarLocked: boolean;
	sidebarOpen: Maybe<boolean>;
	scrolled: boolean;
	
}

export interface SiteContextActions {
	
	toggleSidebar(e?: Event): void;
	openSidebar(e?: Event): void;
	closeSidebar(e?: Event): void;
	
	fetchUpdates(): Promise<void>;
	setActiveCart(cart: ExpandedCart): Promise<void>;

}

export type SiteContext = Signalize<SiteContextData> & SiteContextActions;
export type SiteContextWithAccount = Signalize<SiteContextData & {
	account: OwnAccount;
	user: OwnUser;
	accountAccess: AccountAccess;
	accountTier: Tier;
	context: Context & {
		user: OwnUser;
	};
}> & SiteContextActions;

export const SiteContext = createContext({} as SiteContext);
